import type { ImportTableColumn } from "@domain/Settings";
import type { Job } from "../types";
import type { ValidationContext } from "../types";
import { validateJobsToIssues, type JobIssue } from "./jobValidation";

export type JobValidityStatus = "complete" | "incomplete" | "errors";

export interface JobValidityClassification {
	status: JobValidityStatus;
	/** Fields with a "missing" (required-and-empty) error that is BLOCKING (visible). */
	missingFields: ImportTableColumn[];
	/** Fields with an "invalid" (format) error that is BLOCKING (visible). */
	invalidFields: ImportTableColumn[];
	/** All issues for the job (blocking + non-blocking) — for display/surfacing. */
	issues: JobIssue[];
}

/**
 * Classify each job's validation state into `complete` / `incomplete` / `errors`.
 *
 * Status and the missing/invalid buckets are driven by BLOCKING issues only —
 * an error on a field the user can't see/edit (hidden by company settings, not
 * required) does NOT gate submission. The full `issues` list (blocking +
 * non-blocking) is preserved so callers can still surface everything.
 *
 * `errors` wins over `incomplete` when both are present, since an invalid value
 * is a more severe signal than a merely empty one.
 */
export function classifyJobsValidity(
	jobs: readonly Job[],
	context: ValidationContext,
): Record<string, JobValidityClassification> {
	const issuesByJob = validateJobsToIssues(jobs as Job[], context);
	const out: Record<string, JobValidityClassification> = {};

	for (const job of jobs) {
		const issues = issuesByJob[job.id] ?? [];
		const blocking = issues.filter((i) => i.blocking);
		const missingFields = blocking.filter((i) => i.kind === "missing").map((i) => i.field);
		const invalidFields = blocking.filter((i) => i.kind === "invalid").map((i) => i.field);

		let status: JobValidityStatus = "complete";
		if (invalidFields.length > 0) status = "errors";
		else if (missingFields.length > 0) status = "incomplete";

		out[job.id] = { status, missingFields, invalidFields, issues };
	}

	return out;
}

/** True when any job has a blocking issue (status !== "complete"). */
export function hasBlockingErrors(classifications: Record<string, JobValidityClassification>): boolean {
	return Object.values(classifications).some((c) => c.status !== "complete");
}
